import { NavLink, Link } from "react-router-dom";
import { motion } from "framer-motion";
import logo from '../assets/images/cc.png';
import {
  Mail,
  Phone,
  Globe,
  MapPin,
  ArrowUpRight,
  Heart,
  Zap,
} from "lucide-react";
import { getUserToken } from "../utils/auth";

const footerLinks = [
  {
    title: "Company",
    links: [
      { label: "Home", to: "/" },
      { label: "About", to: "/about" },
      { label: "Services", to: "/services" },
      { label: "Projects", to: "/projects" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "News & Articles", to: "/news" },
      { label: "Contact", to: "/contact" },
      { label: "Privacy Policy", to: "/" },
      { label: "Terms of Service", to: "/" },
    ],
  },
  {
    title: "AI Services",
    links: [
      { label: "AI Chatbot", to: "/services" },
      { label: "Machine Learning", to: "/services" },
      { label: "NLP Solutions", to: "/services" },
      { label: "Generative AI", to: "/services" },
    ],
  },
];

const FooterNew = () => {
  const getStartedPath = getUserToken() ? "/contact" : "/login?redirect=/contact";

  return (
    <footer className="relative overflow-hidden border-t border-white/[0.04]">
      {/* Background glows */}
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[900px] h-[400px] rounded-full opacity-[0.04] blur-[120px] pointer-events-none"
        style={{ background: "radial-gradient(ellipse, #6366F1, transparent)" }}
      />
      <div
        className="absolute bottom-0 right-0 w-[300px] h-[300px] rounded-full opacity-[0.03] blur-[80px] pointer-events-none"
        style={{ background: "radial-gradient(circle, #22D3EE, transparent)" }}
      />

      {/* CTA Banner */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="rounded-2xl p-8 sm:p-14 text-center -mt-1 relative overflow-hidden"
          style={{
            background: "linear-gradient(135deg, rgba(99, 102, 241, 0.12), rgba(34, 211, 238, 0.06), rgba(167, 139, 250, 0.08))",
            border: "1px solid rgba(99, 102, 241, 0.15)",
            boxShadow: "0 4px 40px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.03)",
          }}
        >
          {/* Decorative grid */}
          <div className="absolute inset-0 tech-grid-dense opacity-20 pointer-events-none" />

          {/* Glow accent */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-32 rounded-full opacity-20 blur-[60px] pointer-events-none"
            style={{ background: "radial-gradient(ellipse, #6366F1, transparent)" }}
          />

          <div className="relative z-10">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.5 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Ready to Transform Your Business?
              </h2>
              <p className="text-slate-400 max-w-lg mx-auto mb-8 text-lg leading-relaxed">
                Join leading enterprises who have revolutionized their operations
                with our AI solutions.
              </p>
            </motion.div>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to={getStartedPath} className="neon-btn neon-btn-primary no-underline">
                <Zap className="w-4 h-4" />
                Get Started Today
              </Link>
              <Link to="/projects" className="neon-btn neon-btn-outline no-underline">
                View Our Work
              </Link>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Main footer */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-10">
        <div className="grid lg:grid-cols-5 gap-12 mb-14">
          {/* Brand column */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-3 no-underline mb-6 group">
              <div className="relative">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-accent via-accent-light to-ai-glow flex items-center justify-center shadow-lg">
                   <img src={logo}  alt="ClientCode AI Logo" />
                </div>
                <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-accent to-ai-glow blur-xl opacity-40 group-hover:opacity-60 transition-opacity duration-500" />
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-bold text-white tracking-wide leading-tight">
                  CLIENTCODE AI
                </span>
                <span className="text-[10px] font-semibold tracking-[0.25em] text-ai-glow uppercase">
                  Innovation Pvt. Ltd.
                </span>
              </div>
            </Link>
            {/* <p className="text-slate-400 text-[0.925rem] leading-relaxed mb-7 max-w-sm">
              Transforming businesses with intelligent AI solutions. Pioneering
              Generative AI, Machine Learning, and NLP technologies for
              enterprise success.
            </p> */}
            <div className="space-y-3.5">
              {[
                { icon: Mail, text: "info@clientcodeai.in" },
                { icon: Phone, text: "+91-9958821387" },
                { icon: Globe, text: "www.clientcodeai.com" },
                { icon: MapPin, text: "Faridabad, Haryana, India" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 text-sm text-slate-400 group/item">
                  <div className="p-1.5 rounded-lg transition-all duration-300" style={{
                    background: "rgba(99, 102, 241, 0.06)",
                    border: "1px solid rgba(99, 102, 241, 0.08)",
                  }}>
                    <item.icon className="w-3.5 h-3.5 text-accent-light" />
                  </div>
                  <span className="group-hover/item:text-slate-300 transition-colors">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Link columns */}
          {footerLinks.map((group, i) => (
            <div key={i}>
              <h4 className="text-white font-semibold mb-5 text-sm uppercase tracking-wider">
                {group.title}
              </h4>
              <ul className="space-y-3.5 list-none p-0">
                {group.links.map((link, j) => (
                  <li key={j}>
                    <NavLink
                      to={link.to}
                      className="text-slate-400 hover:text-ai-glow text-sm transition-all duration-300 no-underline flex items-center gap-1.5 group/link"
                    >
                      {link.label}
                      <ArrowUpRight className="w-3 h-3 opacity-0 -translate-y-1 group-hover/link:opacity-100 group-hover/link:translate-y-0 transition-all duration-300" />
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/[0.04] pt-7 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">
            &copy; {new Date().getFullYear()} CLIENTCODE AI Innovation Pvt. Ltd. All
            Rights Reserved.
          </p>
          <p className="text-slate-500 text-xs flex items-center gap-1.5">
            Built with <Heart className="w-3 h-3 text-accent inline" style={{ filter: "drop-shadow(0 0 4px rgba(99, 102, 241, 0.5))" }} /> and AI
          </p>
        </div>
      </div>
    </footer>
  );
};

export default FooterNew;
